import { useState, useMemo, useRef, useEffect } from 'react';
import useStore from '../../store/useStore';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { PageHeader } from '../../components/ui/PageHeader';
import { formatCurrency } from '../../utils/format';

// ─── Inline-editable table cell ────────────────────────────────────────────
function EditableCell({ value, displayValue, highlight, onSave }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const save = () => {
    const num = Number(editVal);
    if (!isNaN(num) && num !== value) onSave(num);
    setEditing(false);
  };

  if (editing) {
    return (
      <Td align="right" className={highlight ? 'bg-accent/8' : ''}>
        <input
          ref={inputRef}
          type="number"
          step="1"
          value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setEditing(false); setEditVal(value); }
          }}
          className="w-28 bg-bg-tertiary border border-accent rounded px-2 py-1 text-sm text-text-primary font-mono text-right focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          autoFocus
        />
      </Td>
    );
  }

  return (
    <Td
      align="right"
      className={`font-mono tabular-nums cursor-pointer hover:bg-accent/10 transition-colors ${highlight ? 'bg-accent/8' : ''}`}
      onClick={() => { setEditing(true); setEditVal(value); }}
      title="Click to edit"
    >
      {displayValue}
    </Td>
  );
}

// ─── Read-only cell (for computed percentages) ─────────────────────────────
function ReadOnlyCell({ value, highlight }) {
  return (
    <Td
      align="right"
      className={`font-mono tabular-nums text-text-secondary ${highlight ? 'bg-accent/8' : ''}`}
    >
      {value}
    </Td>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function Taxes() {
  const taxes = useStore((s) => s.taxes);
  const upsertTaxYear = useStore((s) => s.upsertTaxYear);
  const balanceSheetMonthly = useStore((s) => s.balanceSheetMonthly);

  // Sort tax years ascending
  const sortedYears = useMemo(
    () => [...taxes].sort((a, b) => a.year - b.year),
    [taxes],
  );

  // Build a map: year → December net worth from balance_sheet_monthly
  const netWorthByYear = useMemo(() => {
    const map = {};
    for (const row of balanceSheetMonthly) {
      const month = row.month; // e.g. '2024-12'
      if (month && month.endsWith('-12')) {
        const yr = parseInt(month.slice(0, 4), 10);
        map[yr] = row.netWorth || 0;
      }
    }
    return map;
  }, [balanceSheetMonthly]);

  const lastYear = sortedYears.length > 0 ? sortedYears[sortedYears.length - 1].year : null;

  const fmtPct = (num) => {
    if (num == null || isNaN(num) || !isFinite(num)) return '—';
    return `${num.toFixed(2)}%`;
  };

  const handleSave = (year, field, value) => {
    const existing = taxes.find((t) => t.year === year) || { year };
    upsertTaxYear({ ...existing, [field]: value });
  };

  return (
    <div>
      <PageHeader title="Taxes" subtitle="Annual federal tax summary" />

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <Thead>
              <Tr>
                <Th align="left" className="min-w-[260px]">Taxes</Th>
                {sortedYears.map((t) => (
                  <Th
                    key={t.year}
                    align="right"
                    className={`min-w-[120px] ${t.year === lastYear ? 'bg-accent/8' : ''}`}
                  >
                    {t.year}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <tbody>
              {/* Adjusted Gross Income — editable */}
              <Tr>
                <Td className="font-medium text-text-primary">Adjusted Gross Income</Td>
                {sortedYears.map((t) => (
                  <EditableCell
                    key={t.year}
                    value={t.adjustedGrossIncome || 0}
                    displayValue={formatCurrency(t.adjustedGrossIncome || 0)}
                    highlight={t.year === lastYear}
                    onSave={(v) => handleSave(t.year, 'adjustedGrossIncome', v)}
                  />
                ))}
              </Tr>

              {/* Taxable Income — editable */}
              <Tr>
                <Td className="font-medium text-text-primary">Taxable Income</Td>
                {sortedYears.map((t) => (
                  <EditableCell
                    key={t.year}
                    value={t.taxableIncome || 0}
                    displayValue={formatCurrency(t.taxableIncome || 0)}
                    highlight={t.year === lastYear}
                    onSave={(v) => handleSave(t.year, 'taxableIncome', v)}
                  />
                ))}
              </Tr>

              {/* Federal Tax — editable */}
              <Tr>
                <Td className="font-medium text-text-primary">Federal Tax</Td>
                {sortedYears.map((t) => (
                  <EditableCell
                    key={t.year}
                    value={t.federalTax || 0}
                    displayValue={formatCurrency(t.federalTax || 0)}
                    highlight={t.year === lastYear}
                    onSave={(v) => handleSave(t.year, 'federalTax', v)}
                  />
                ))}
              </Tr>

              {/* ── Computed Rates ─────────────────────────────────── */}

              {/* Effective Gross Income Tax Rate */}
              <Tr>
                <Td className="text-text-secondary">Effective Gross Income Tax Rate</Td>
                {sortedYears.map((t) => {
                  const rate = t.adjustedGrossIncome
                    ? (t.federalTax / t.adjustedGrossIncome) * 100
                    : 0;
                  return (
                    <ReadOnlyCell
                      key={t.year}
                      value={fmtPct(rate)}
                      highlight={t.year === lastYear}
                    />
                  );
                })}
              </Tr>

              {/* Effective Taxable Income Tax Rate */}
              <Tr>
                <Td className="text-text-secondary">Effective Taxable Income Tax Rate</Td>
                {sortedYears.map((t) => {
                  const rate = t.taxableIncome
                    ? (t.federalTax / t.taxableIncome) * 100
                    : 0;
                  return (
                    <ReadOnlyCell
                      key={t.year}
                      value={fmtPct(rate)}
                      highlight={t.year === lastYear}
                    />
                  );
                })}
              </Tr>

              {/* Effective Wealth Tax Rate */}
              <Tr>
                <Td className="text-text-secondary">Effective Wealth Tax Rate</Td>
                {sortedYears.map((t) => {
                  const nw = netWorthByYear[t.year] || 0;
                  const rate = nw ? (t.federalTax / nw) * 100 : 0;
                  return (
                    <ReadOnlyCell
                      key={t.year}
                      value={fmtPct(rate)}
                      highlight={t.year === lastYear}
                    />
                  );
                })}
              </Tr>
            </tbody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
